import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PopoverRoot, PopoverTrigger, PopoverPositioner, PopoverContent, PopoverBody } from '@chakra-ui/react';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications, type AppNotification } from '@/hooks/useNotifications';
import { getNotificationNavigationUrl } from '@/utils/notificationNavigation';
import { BottomSheet } from '@/components/common/BottomSheet';
import { BellButton } from './BellButton';
import { NotificationPanel } from './NotificationPanel';

export type VercelNotificationPopoverProps = {
  userId?: string;
  authLoading?: boolean;
};

function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);
  return isMobile;
}

export function VercelNotificationPopover({ userId: userIdProp, authLoading: authLoadingProp }: VercelNotificationPopoverProps) {
  const navigate = useNavigate();
  const titleId = useId();
  const { user: authUser, loading: authLoadingFromContext } = useAuth();
  const userId = userIdProp ?? authUser?.id;
  const authLoading = authLoadingProp ?? authLoadingFromContext;

  const { notifications, loading, error, markAsRead, markAllAsRead, deleteNotification, refetch } =
    useNotifications(userId, authLoading);
  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const isMobile = useIsMobile(768);
  const [open, setOpen] = useState(false);

  // Opening the panel only reloads it. Looking at the list is not reading it: a row stays
  // unread -- and keeps counting on the bell and the device icon badge -- until it is
  // tapped, which marks that one row read in handleActivate.
  useEffect(() => {
    if (open) void refetch();
  }, [open, refetch]);

  const close = useCallback(() => {
    setOpen(false);
    // Nothing reaches this list live, so reload to count what arrived while it was open.
    void refetch();
  }, [refetch]);

  const handleActivate = useCallback(
    async (n: AppNotification) => {
      if (!n.read) await markAsRead(n.id);
      const url = getNotificationNavigationUrl(n);
      close();
      if (url) navigate(url);
    },
    [markAsRead, close, navigate],
  );

  const handleSeeAll = useCallback(() => {
    close();
    navigate('/notifications');
  }, [close, navigate]);

  const panelProps = {
    titleId,
    notifications,
    loading,
    error,
    onActivate: (n: AppNotification) => void handleActivate(n),
    onDelete: deleteNotification,
    onMarkAllRead: markAllAsRead,
    onSeeAll: handleSeeAll,
    onClose: close,
  };

  if (isMobile) {
    return (
      <>
        <BellButton unreadCount={unreadCount} open={open} onClick={() => setOpen(true)} />
        <BottomSheet open={open} onClose={close} labelledBy={titleId} data-notification-popover>
          <NotificationPanel variant="sheet" {...panelProps} />
        </BottomSheet>
      </>
    );
  }

  return (
    <PopoverRoot
      open={open}
      onOpenChange={(e) => (e.open ? setOpen(true) : close())}
      positioning={{ placement: 'bottom-end', gutter: 8 }}
      // Focusing the first row on open would reveal its trash button (focus-within);
      // focus stays on the bell and Tab moves into the list.
      autoFocus={false}
    >
      <PopoverTrigger asChild>
        <BellButton unreadCount={unreadCount} open={open} />
      </PopoverTrigger>
      <PopoverPositioner>
        <PopoverContent
          aria-labelledby={titleId}
          w="400px"
          maxW="calc(100vw - 24px)"
          overflow="hidden"
          borderRadius="16px"
          border="1px solid"
          borderColor="volyx.borderLight"
          boxShadow="0 12px 32px rgba(31, 41, 50, 0.16)"
          // Drops down out of the bell rather than the recipe's default scale-in.
          _open={{ animationStyle: 'slide-fade-in', animationDuration: 'moderate' }}
          _closed={{ animationStyle: 'slide-fade-out', animationDuration: 'fast' }}
          css={{
            '@media (prefers-reduced-motion: reduce)': {
              '&[data-state=open][data-placement]': { animationName: 'fade-in' },
              '&[data-state=closed][data-placement]': { animationName: 'fade-out' },
            },
          }}
          data-notification-popover
        >
          <PopoverBody p={0} maxH="min(560px, calc(100dvh - 96px))" display="flex" flexDirection="column">
            <NotificationPanel variant="popover" {...panelProps} />
          </PopoverBody>
        </PopoverContent>
      </PopoverPositioner>
    </PopoverRoot>
  );
}
